import { noop } from './chunk-SGGNWCK6.mjs';
import { errorProxy } from './chunk-F4TFYERD.mjs';
import { accountSchema } from './chunk-2CIDNLJ5.mjs';
import { UnknownError, AuthenticationError, TooManyRequestsError, UserDisabledError } from './chunk-DGS6LEDH.mjs';
import { object, string } from 'zod';
import { onAuthStateChanged, getIdTokenResult, signInWithPopup, signInWithEmailAndPassword, signOut, updateProfile, getIdToken, GoogleAuthProvider, TwitterAuthProvider } from 'firebase/auth';
import { fromPromise, okAsync, err } from 'neverthrow';
import { Observable, switchMap, firstValueFrom } from 'rxjs';

object({
  displayName: string(),
  photoURL: string().url()
});
var accountOutputSchema = accountSchema;
var accountBlocFactory = ({ auth }) => {
  const authState = new Observable((subscriber) => {
    return onAuthStateChanged(auth, {
      next: (user) => subscriber.next(user),
      error: (error) => subscriber.error(error),
      complete: () => subscriber.complete()
    });
  });
  const stream = () => {
    return authState.pipe(
      switchMap(async (user) => {
        if (!user) return user;
        const result = await getIdTokenResult(user);
        const roles = Array.isArray(result.claims.roles) ? result.claims.roles : [];
        return accountOutputSchema.parse({
          id: user.uid,
          displayName: user.displayName,
          photoURL: user.photoURL,
          roles,
          get loginMethod() {
            var _a;
            switch ((_a = user.providerData.at(0)) == null ? void 0 : _a.providerId) {
              case "twitter.com":
                return "twitter";
              case "google.com":
                return "google";
              case "password":
                return "password";
              default:
                return null;
            }
          }
        });
      })
    );
  };
  const getSignInProvider = (type) => {
    switch (type) {
      case "twitter":
        return new TwitterAuthProvider();
      case "google":
        return new GoogleAuthProvider();
    }
  };
  const signIn = (type) => {
    const provider = getSignInProvider(type);
    return fromPromise(signInWithPopup(auth, provider), (e) => {
      if (e instanceof Error) return new UnknownError("singIn failed", { cause: e });
      return new UnknownError("singIn failed");
    }).map(noop);
  };
  const _signInWithEmailPassword = (email, password) => {
    return fromPromise(signInWithEmailAndPassword(auth, email, password), (e) => {
      const errorCode = e.code;
      switch (errorCode) {
        case "auth/user-disabled":
          return new UserDisabledError("User account is disabled", { cause: e });
        case "auth/too-many-requests":
          return new TooManyRequestsError("Too many login attempts", { cause: e });
        default:
          return e instanceof Error ? new AuthenticationError("Invalid email or password", { cause: e }) : new UnknownError("signIn failed");
      }
    }).map(noop);
  };
  const _signOut = () => {
    return fromPromise(signOut(auth), (e) => {
      if (e instanceof Error) return new UnknownError("singOut failed", { cause: e });
      return new UnknownError("singOut failed");
    });
  };
  const edit = (data) => {
    return fromPromise(firstValueFrom(authState), errorProxy("fetch auth state failed")).andThen((user) => user ? okAsync(user) : err(new AuthenticationError())).andThen((user) => fromPromise(updateProfile(user, data), errorProxy("update profile failed")));
  };
  const getToken = (refresh) => {
    return fromPromise(firstValueFrom(authState), errorProxy("fetch auth state failed")).andThen((user) => {
      return user ? fromPromise(getIdToken(user, refresh), errorProxy("get token failed")) : err(new AuthenticationError());
    });
  };
  return {
    stream,
    signIn,
    signInWithEmailPassword: _signInWithEmailPassword,
    signOut: _signOut,
    edit,
    getToken
  };
};

export { accountBlocFactory, accountOutputSchema };
//# sourceMappingURL=chunk-PRXZQZGO.mjs.map
//# sourceMappingURL=chunk-PRXZQZGO.mjs.map